import {
  Box,
  BreadcrumbList,
  Button,
  Checkbox,
  Container,
  Flex,
  Link,
  Typography,
} from '@fe-toolkit/web-components/ssr'

import { ComponentProps, useCallback, useState } from 'react'

import { customEvent } from '@fe-toolkit/eventing'

import { ContractDocumentsList, ProgressSteps } from '@giffgaff/broadband-common'

import { Plan } from '@/api'

import theme from '@/theme/theme'

import { useEffectOnceWithCondition } from '@/hooks/useEffectOnceWithCondition'

import getChangePlanPreviewEventEntity from '@/utils/eventing/getChangePlanPreviewEventEntity'

import { basePath } from '@/constants'

import { useGenerateContract } from '@/hooks/apiHooks'

import ProfilePageLoader from '@/components/molecules/ProfilePageLoader'

import ChangePlanComparison from '../components/ChangePlanComparison'
import ChangePlanNoContent from '../components/ChangePlanNoContent'
import ChangePlanNotification from '../components/ChangePlanNotification'
import ChangePlanPaymentDetails from '../components/ChangePlanPaymentDetails'
import { ConfirmPlanChangeState, ChangePlanErrorCode } from '../types'
import { changePlanProgressSteps, highlight } from '../consts'
import formatPlanPaymentAttributes from '../utils/formatPlanPaymentAttributes'
import ChangePlanConfirmErrorNotification from './components/ChangePlanConfirmErrorNotification'

type BaseChangePlanConfirmProps = {
  onConfirm: () => void
  oldPlanId?: string
  draftPlanId: string
  plan?: Plan
}

export type ChangePlanConfirmProps = BaseChangePlanConfirmProps & ConfirmPlanChangeState

const ChangePlanConfirm = ({
  status,
  planChangePreview,
  ecomOrderSchedule,
  plan,
  onConfirm,
  errorCode,
  oldPlanId,
  draftPlanId,
}: ChangePlanConfirmProps) => {
  const [termsAccepted, setTermsAccepted] = useState(false)
  const newPlan = planChangePreview?.newPlan
  const oldPlan = planChangePreview?.oldPlan
  const hasPlans = !!newPlan
  const newContractRequired = planChangePreview?.newContractRequired

  const { isLoading: isGenerateContractPending, error: generateContractError } = useGenerateContract(
    {
      planId: draftPlanId,
    },
    {
      enabled: !!planChangePreview?.newContractRequired,
      queryKey: ['generateContract', draftPlanId],
    },
  )

  useEffectOnceWithCondition(
    ([oldPlanId, planChangePreview]) => {
      customEvent('bb_change_plan_journey_impression', {
        contextType: 'confirm_plan',
        currentPlanId: oldPlanId ?? '',
        newPlan: getChangePlanPreviewEventEntity(planChangePreview),
      })
    },
    ([_0, _1, status]) => status !== 'idle' && status !== 'loading' && status !== 'confirming',
    [oldPlanId, planChangePreview, status] as const,
  )

  const handleOnConfirm = useCallback(() => {
    customEvent('bb_change_plan_journey_cta_click', {
      contextType: 'confirm_plan',
      currentPlanId: oldPlanId ?? '',
      newPlan: getChangePlanPreviewEventEntity(planChangePreview),
    })
    onConfirm()
  }, [oldPlanId, onConfirm, planChangePreview])

  const planInfo = formatPlanPaymentAttributes(planChangePreview, ecomOrderSchedule)

  return (
    <>
      {!!errorCode && <ChangePlanConfirmErrorNotification {...getErrorCopy(errorCode)} planInfo={planInfo} />}
      <Container
        as="section"
        backgroundColor="wildSand"
        sx={({ theme }) => ({
          padding: `${theme.spacing.medium} 0`,
          [theme.breakpoints.up('medium')]: {
            padding: `${theme.spacing.large} 0`,
          },
        })}
      >
        <BreadcrumbList
          items={[
            {
              href: basePath,
              name: 'Account',
              onClick: () => {
                customEvent('gg_click', { contextType: 'account' })
              },
            },
            { name: 'Change plan' },
          ]}
        />
        <Box
          sx={({ theme }) => ({
            marginTop: theme.spacing.medium,
            [theme.breakpoints.up('medium')]: {
              marginTop: theme.spacing.large,
            },
          })}
        >
          <ProgressSteps currentStep={1} steps={changePlanProgressSteps}></ProgressSteps>
        </Box>

        {oldPlanId && (
          <Box
            as="p"
            sx={({ theme }) => ({
              marginTop: theme.spacing.medium,
              [theme.breakpoints.up('medium')]: {
                marginTop: theme.spacing.large,
              },
            })}
          >
            <Link href={`${basePath}change-plan/${oldPlanId}`}>Back</Link>
          </Box>
        )}

        <Typography
          as="h1"
          sx={({ theme }) => ({
            marginTop: theme.spacing.medium,
            [theme.breakpoints.up('medium')]: {
              marginTop: theme.spacing.large,
            },
          })}
          testId="change-plan-confirm-title"
          variant="megaphone"
        >
          Change plan
        </Typography>

        <Typography as="p" marginTop="medium" variant="speak">
          Make sure everything’s spot on before we go ahead.
        </Typography>

        <Box marginTop="large">
          {status === 'loading' && <ProfilePageLoader />}

          {status === 'empty' && (
            <Box my="medium">
              <ChangePlanNoContent>No plan change information available</ChangePlanNoContent>
            </Box>
          )}

          {status !== 'loading' && hasPlans && (
            <Flex flexDirection="column" gap={theme.spacing.medium} maxWidth={theme.layout.readableWidth}>
              <ChangePlanComparison newPlan={newPlan} oldPlan={oldPlan} plan={plan} />

              <ChangePlanPaymentDetails plan={plan} planInfo={planInfo} />

              <ChangePlanNotification>
                <Typography as="p" data={planInfo} variant="speakUp">
                  {highlight[planInfo.changeType].title}
                </Typography>
                <Typography as="p" data={planInfo} variant="speak">
                  {highlight[planInfo.changeType].copy}
                </Typography>
              </ChangePlanNotification>

              {newContractRequired ? (
                <>
                  <Typography as="h3" variant="louder">
                    One last thing...
                  </Typography>
                  <Box>
                    <ContractDocumentsList
                      error={generateContractError}
                      isPending={isGenerateContractPending}
                      planId={draftPlanId}
                    />
                    <Box
                      backgroundColor={theme.colors.white}
                      borderRadius={theme.borderRadius.small}
                      marginBottom={theme.spacing.medium}
                      px={theme.spacing.small}
                      py={theme.spacing.tiny}
                    >
                      <Checkbox
                        checked={termsAccepted}
                        labelText="I understand and agree"
                        onChange={event => setTermsAccepted(event.target.checked)}
                      />
                    </Box>
                  </Box>
                </>
              ) : (
                <Typography as="p" variant="speak">
                  Your plan’s changing, but your contract stays exactly the same as before.
                </Typography>
              )}

              {/* Confirm plan button */}
              <Button
                isDisabled={!termsAccepted && newContractRequired}
                isLoading={status === 'confirming'}
                sx={({ theme }) => ({
                  marginBottom: theme.spacing.medium,
                  alignSelf: 'flex-start',
                })}
                variant="primary"
                onClick={handleOnConfirm}
              >
                {planInfo.paymentRequired ? 'Check payment details' : 'Confirm plan change'}
              </Button>
            </Flex>
          )}
        </Box>
      </Container>
    </>
  )
}

const errors = {
  paymentFailed: {
    title: 'Your payment didn’t go through',
    copy: 'Something went wrong with your payment. Check your details and try again.',
  },
  tooCloseToPlanStart: {
    title: 'You can only change your plan once a month',
    copy: 'Your plan’s recently been sorted. You’ll be able to change it again from {{canChangeFromDate}}.',
  },
  tooCloseToNextBill: {
    title: 'You can’t change your plan right now',
    copy: `As your plan is due to refresh in the next couple of hours, we can’t make changes at this point.`,
  },
  couldNotFetchData: { title: 'We couldn’t find this plan change', copy: 'Please try again later' },
  unknown: { title: 'You can’t change your plan right now', copy: '' },
} as const

function getErrorCopy(
  code: ChangePlanErrorCode,
): Omit<ComponentProps<typeof ChangePlanConfirmErrorNotification>, 'planInfo'> {
  return code && code in errors ? errors[code] : errors.unknown
}

export default ChangePlanConfirm
